/**
 * PerformanceMetrics Component
 * Real-time performance dashboard with fleet efficiency visualization
 * Integrates with existing React Query hooks for data fetching
 */

import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { BentoGrid, BentoGridItem } from '../common/BentoGrid';
import { DashboardMetricsCard } from './DashboardMetricsCard';
import { MagicCard } from '../ui/magic/MagicCard';
import { NumberTicker } from '../ui/magic/NumberTicker';
import { cn } from '../../lib/utils';

// Hooks for real-time data integration
import { useVehicles } from '../../hooks/useVehicles';
import { useProjects } from '../../hooks/useProjects';
import { useProject } from '../../contexts/ProjectContext';

// Icons
import {
  TrendingUp,
  TrendingDown,
  Truck,
  MapPin,
  Clock,
  DollarSign,
  Leaf,
  Zap,
  Activity,
  Target,
} from 'lucide-react';

export interface PerformanceMetricsProps {
  className?: string;
  showRealTimeUpdates?: boolean;
  compactMode?: boolean;
}

// Helper function to calculate fleet efficiency
const calculateFleetEfficiency = (vehicles: any[] = []) => {
  if (!vehicles.length) return 0;
  
  const activeVehicles = vehicles.filter(v => v.capacity && v.capacity.length > 0);
  const totalCapacity = activeVehicles.reduce((sum, v) => 
    sum + (v.capacity?.[0] || 0), 0
  );
  const utilizationRate = activeVehicles.length / vehicles.length;
  
  return Math.round(utilizationRate * 85 + (totalCapacity > 0 ? 15 : 0));
};

// Helper function to calculate cost savings
const calculateCostSavings = (vehicles: any[] = [], projects: any[] = []) => {
  const vehicleCount = vehicles.length;
  const projectCount = projects.length;
  
  // Mock calculation based on optimization efficiency
  const baselineCost = vehicleCount * 1000 + projectCount * 500;
  const optimizedSavings = Math.min(vehicleCount * 120 + projectCount * 80, baselineCost * 0.3);
  
  return Math.round(optimizedSavings);
};

// Helper function to calculate carbon footprint reduction
const calculateCarbonReduction = (vehicles: any[] = []) => {
  const electricVehicles = vehicles.filter(v => 
    v.description?.toLowerCase().includes('electric') || 
    v.description?.toLowerCase().includes('ev')
  ).length;
  
  const totalVehicles = vehicles.length || 1;
  const electricRatio = electricVehicles / totalVehicles;
  
  // Base reduction from route optimization + additional from electric vehicles
  return Math.round(45 + (electricRatio * 35));
};

export const PerformanceMetrics: React.FC<PerformanceMetricsProps> = ({
  className,
  showRealTimeUpdates = true,
  compactMode = false,
}) => {
  const { currentProject } = useProject();
  const { data: projects = [] } = useProjects();
  const { 
    data: vehicles = [], 
    isLoading: vehiclesLoading,
    error: vehiclesError 
  } = useVehicles(currentProject?.id || 0);

  // Calculate real-time metrics
  const metrics = useMemo(() => {
    const fleetEfficiency = calculateFleetEfficiency(vehicles);
    const costSavings = calculateCostSavings(vehicles, projects);
    const carbonReduction = calculateCarbonReduction(vehicles);
    const routeOptimization = Math.min(fleetEfficiency + 5, 100);
    
    return {
      fleetEfficiency,
      costSavings,
      carbonReduction,
      routeOptimization,
      activeVehicles: vehicles.filter((v: any) => v.capacity && v.capacity.length > 0).length,
      totalProjects: projects.length,
      avgResponseTime: 2.1 + Math.random() * 0.8, // Simulated real-time metric
    };
  }, [vehicles, projects]);

  // Metrics configuration
  const metricsConfig = [
    {
      title: 'Fleet Efficiency',
      value: metrics.fleetEfficiency,
      max: 100,
      suffix: '%',
      icon: <TrendingUp className="w-5 h-5" />,
      color: 'green' as const,
      displayType: 'both' as const,
      trend: { 
        direction: (metrics.fleetEfficiency > 80 ? 'up' : 'neutral') as 'up' | 'neutral', 
        value: Math.round(metrics.fleetEfficiency * 0.1), 
        suffix: '%' 
      },
      priority: 'high' as const,
    },
    {
      title: 'Cost Savings',
      value: metrics.costSavings,
      prefix: '$',
      icon: <DollarSign className="w-5 h-5" />,
      color: 'blue' as const,
      displayType: 'number' as const,
      trend: { 
        direction: 'up' as const, 
        value: Math.round(metrics.costSavings * 0.08), 
        suffix: '' 
      },
      priority: 'high' as const,
    },
    {
      title: 'CO₂ Reduction',
      value: metrics.carbonReduction,
      max: 100,
      suffix: '%',
      icon: <Leaf className="w-5 h-5" />,
      color: 'green' as const,
      displayType: 'both' as const,
      trend: { 
        direction: 'up' as const, 
        value: Math.round(metrics.carbonReduction * 0.12), 
        suffix: '%' 
      },
      priority: 'high' as const,
    },
    {
      title: 'Route Optimization',
      value: metrics.routeOptimization,
      max: 100,
      suffix: '%',
      icon: <Target className="w-5 h-5" />,
      color: 'purple' as const,
      displayType: 'both' as const,
      trend: { 
        direction: 'up' as const, 
        value: 7, 
        suffix: '%' 
      },
      priority: 'medium' as const,
    },
    {
      title: 'Active Vehicles',
      value: metrics.activeVehicles,
      icon: <Truck className="w-5 h-5" />,
      color: 'amber' as const,
      displayType: 'number' as const,
      trend: { 
        direction: 'neutral' as const, 
        value: 0, 
        suffix: '' 
      },
      priority: 'medium' as const,
    },
    {
      title: 'Response Time',
      value: Number(metrics.avgResponseTime.toFixed(1)),
      suffix: 's',
      icon: <Clock className="w-5 h-5" />,
      color: 'blue' as const,
      displayType: 'number' as const,
      trend: { 
        direction: (metrics.avgResponseTime < 2.5 ? 'down' : 'up') as 'down' | 'up', 
        value: 0.2, 
        suffix: 's' 
      },
      priority: 'low' as const,
    },
  ];

  if (vehiclesLoading) {
    return (
      <MagicCard className={cn('p-6', className)}>
        <div className="flex items-center justify-center space-x-3">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
          <span className="text-gray-600">Loading performance metrics...</span>
        </div>
      </MagicCard>
    );
  }

  if (vehiclesError) {
    return (
      <MagicCard className={cn('p-6 border-red-200 bg-red-50', className)}>
        <div className="flex items-center space-x-3">
          <TrendingDown className="w-6 h-6 text-red-600" />
          <div>
            <h3 className="text-red-800 font-medium">Error Loading Metrics</h3>
            <p className="text-red-600 text-sm">Unable to fetch performance data</p>
          </div>
        </div>
      </MagicCard>
    );
  }

  if (compactMode) {
    return (
      <div className={cn('grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3', className)}>
        {metricsConfig.map((metric, index) => (
          <motion.div
            key={metric.title}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: index * 0.1 }}
          >
            <DashboardMetricsCard {...metric} className="h-full" />
          </motion.div>
        ))}
      </div>
    );
  }

  return (
    <div className={cn('space-y-6', className)}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Performance Metrics</h2>
          <p className="text-gray-600">
            Real-time insights for {currentProject?.name || 'your VRP system'}
          </p>
        </div>
        
        {showRealTimeUpdates && (
          <motion.div
            className="flex items-center space-x-2 px-3 py-1 bg-green-100 rounded-full"
            animate={{ scale: [1, 1.05, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <Activity className="w-4 h-4 text-green-600" />
            <span className="text-sm font-medium text-green-700">Live</span>
          </motion.div>
        )}
      </div>

      {/* Metrics Grid */}
      <BentoGrid columns={{ sm: 1, md: 2, lg: 3, xl: 3 }} gap={4}>
        {/* Primary KPIs - Larger cards */}
        {metricsConfig.slice(0, 3).map((metric) => (
          <BentoGridItem 
            key={metric.title}
            priority={metric.priority}
            className="min-h-[200px]"
          >
            <DashboardMetricsCard {...metric} />
          </BentoGridItem>
        ))}

        {/* Secondary Metrics */}
        {metricsConfig.slice(3).map((metric) => (
          <BentoGridItem 
            key={metric.title}
            priority={metric.priority}
            className="min-h-[160px]"
          >
            <DashboardMetricsCard {...metric} />
          </BentoGridItem>
        ))}
      </BentoGrid>

      {/* Summary Statistics */}
      <MagicCard className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <Zap className="w-8 h-8 text-amber-500 mx-auto mb-2" />
            <NumberTicker
              value={Math.round((metrics.fleetEfficiency + metrics.routeOptimization) / 2)}
              suffix="%"
              className="text-2xl font-bold text-gray-900"
            />
            <p className="text-sm text-gray-600">Overall System Efficiency</p>
          </div>
          
          <div className="text-center">
            <MapPin className="w-8 h-8 text-blue-500 mx-auto mb-2" />
            <NumberTicker
              value={vehicles.length}
              className="text-2xl font-bold text-gray-900"
            />
            <p className="text-sm text-gray-600">Total Fleet Size</p>
          </div>
          
          <div className="text-center">
            <Leaf className="w-8 h-8 text-green-500 mx-auto mb-2" />
            <NumberTicker
              value={Math.round(metrics.costSavings * 0.05)}
              prefix="$"
              className="text-2xl font-bold text-gray-900"
            />
            <p className="text-sm text-gray-600">Environmental Savings</p>
          </div>
        </div>
      </MagicCard>
    </div>
  );
};

export default PerformanceMetrics;